import type { Eta } from "@veyso/core";
import { authHeader, handleUnauthenticated } from "./auth/token.js";
import type {
  BrandKit,
  CaptionCue,
  LanguageDepth,
  PipelineStep,
  StepDiff,
  ShareStats,
  Timeline,
  TranslatedText,
} from "@veyso/core";
import type { ContentProvider } from "./lib/providers.js";

export interface ApiErrorBody {
  error: string;
  code: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Sends a call with the auth header and answers with the response, or throws the API's error. */
async function send(path: string, init?: RequestInit, isRetry = false): Promise<Response> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}),
      // Before the caller's own headers, so a call that sets its own
      // authorization for some reason still wins. Empty when no auth provider
      // is configured, which is the open mode.
      ...authHeader(),
      ...init?.headers,
    },
  });

  if (response.status === 401 && !isRetry) {
    /**
     * Ask the provider to re-check the session, then try once more.
     *
     * Two things reach here. A token that had gone stale while the page was
     * open, which a refresh fixes — so the call succeeds on the second attempt
     * and nobody sees anything but a slower request. And a request that went
     * out with no token at all, which used to happen on the first fetch of a
     * fresh page load.
     *
     * It used to hang here instead, on a promise that never settled. That was
     * right when this redirected to a hosted sign-in page: the browser was
     * leaving and rejecting would have painted an error on the way out. It
     * refreshes in place now, so nothing leaves and hanging just leaves a
     * screen blank for ever — which is exactly what the billing panel did on
     * the way back from Stripe.
     */
    if (await handleUnauthenticated()) return send(path, init, true);

    // Nothing to redirect to means the API requires sign-in and this app was
    // never told where that happens. That is a deployment half-configured, and
    // saying so is more use than repeating the API's answer.
    throw new ApiError(
      "This deployment requires sign-in, but the portal has no auth provider configured. " +
        "Set VEYSO_PROPELAUTH_URL on the web app to the same URL as the API's PROPELAUTH_AUTH_URL.",
      401,
      "auth_misconfigured",
    );
  }

  if (!response.ok) {
    let body: ApiErrorBody = { error: response.statusText, code: "http_error" };
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      // The API answers every error with a JSON `{ error, code }` — its own 500
      // reads "Something went wrong". So a 5xx whose body is not JSON did not
      // come from the API at all: it is the dev server proxying to nothing, or
      // a gateway in front of a process that is down. Reporting the status
      // text there — "Internal Server Error" — names the wrong machine and
      // sends people looking for a bug in a request that was never delivered.
      if (response.status >= 500) {
        body = { error: "Could not reach the API — is the server running?", code: "unreachable" };
      }
    }
    throw new ApiError(body.error, response.status, body.code, body.details);
  }

  return response;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await send(path, init);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** For a file the browser should save: the same call, with the body kept as bytes. */
async function requestBlob(path: string, init?: RequestInit): Promise<Blob> {
  return (await send(path, init)).blob();
}

export interface ProjectSummary {
  id: string;
  name: string;
  /**
   * What the project is for, which decides where its card goes.
   *
   * A launch video has no timeline behind it, so its card must not open the
   * editor — see the routing in `Dashboard`.
   */
  kind: "demo" | "launch";
  status: string;
  /** Set only for a finished launch video: the file itself. */
  downloadUrl: string | null;
  /** The same finished launch video, to play rather than download. */
  videoUrl: string | null;
  createdAt: string;
  updatedAt: string;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
  hasOutput: boolean;
  /**
   * What the customer was charged for this project, in US dollars — the same
   * figure the billing statement shows. Null until it has been charged.
   */
  chargedUsd: number | null;
  /** The video used a free allowance rather than credit. */
  freeVideo: boolean;
}

export interface Narrator {
  id: string;
  name: string;
  /** "british, middle_aged, narrative_story" — whatever the account has tagged. */
  description: string;
  previewUrl: string | null;
}

/** One version of a launch video, as the studio shows it. */
export interface LaunchVersionView {
  id: string;
  number: number;
  status: "making" | "ready" | "failed" | "removed";
  /** What was asked for: "First version", "Your script edits", or the customer's words. */
  request: string;
  /** How it came about: the first film, a script edit, a chat message, or a failed first film tried again. */
  source: "first" | "script" | "chat" | "retry";
  /** What changed, said back; null until it is made, and for a script edit. */
  summary: string | null;
  /** Why it could not be made, short; only on a failed version. */
  error: string | null;
  priceUsd: number;
  durationSeconds: number | null;
  videoUrl: string | null;
  downloadUrl: string | null;
  thumbnailUrl: string | null;
  createdAt: string;
  /** While it is being made: how far along, the step it is on and the time left. Null otherwise. */
  progress: number | null;
  step: string | null;
  eta: Eta | null;
  /** The job making it, while it is being made, so the page and the jobs menu count it down as one. */
  jobId?: string | null;
}

/** Everything the studio needs, in one read. */
export interface LaunchVersionsView {
  /** Newest first, every status included. */
  versions: LaunchVersionView[];
  /** The newest ready version's voice-over. */
  script: string;
  /** About how many words fit this film's length. */
  scriptBudget: number;
  /** Words a second the narrator speaks, so a `[1s]` pause counts against the budget. */
  scriptPace?: number;
  /** What the next version will cost. */
  quoteUsd: number;
  /** What is left after credit held for versions being made; null when nothing is billed. */
  balanceUsd: number | null;
  /** The team is free forever: every version is free. */
  freeForever?: boolean;
  making: boolean;
  /** A failed first film, with nothing ever made: it can be tried again from its newest version. */
  canRetryFirst: boolean;
}

export interface ProjectDetail {
  id: string;
  name: string;
  kind: "demo" | "launch";
  status: string;
  /** Whose it is. Already in the payload (the route spreads the row); typed so an admin can tell another team's project. */
  orgId: string | null;
  /**
   * What a launch video was made from. Null for an ordinary project.
   *
   * Named fields rather than the job payload: the payload carries the logo
   * inline as base64, and a screen that edits a brief should edit a brief
   * rather than whatever the queue happens to store.
   */
  launch?: {
    templateId: string | null;
    name: string;
    website: string;
    about: string;
    logo: "image" | "wordmark";
    free: boolean;
    /** The voice-over the last run read, when it had one. */
    script: string | null;
    /** True when a person wrote it rather than the model. */
    scriptEdited: boolean;
    /** The word limit for this length and voice — a guide for editing. */
    scriptBudget: number | null;
    /** Why the film is silent, when it is. */
    silentBecause: string | null;
    /** How the newest attempt ended — it may have failed over an older film. */
    lastAttempt: { status: string; error: string | null } | null;
  } | null;
  tone: string | null;
  audience: string | null;
  purpose: string | null;
  instructions: string | null;
  /** Overrides the global script model for this project. Null means inherit. */
  scriptModel: string | null;
  /** Present when auto-cut is on: the ranges of the recording that survived. */
  cutPlan: { keeps: { start: number; end: number }[]; keptSeconds: number; removedSeconds: number } | null;
  /** Whether this project's cuts can come from the screen, so a voice switch can change them. */
  frameCuts: boolean;
  createdAt: string;
  updatedAt: string;
  assets: {
    id: string;
    key: string;
    kind: string;
    durationSeconds: number | null;
    width: number | null;
    height: number | null;
    filename: string;
    /** Already in the payload; typed here so the download can state a size. */
    sizeBytes: number;
  }[];
  transcript: { id: string; provider: string; language: string | null } | null;
  scripts: { id: string; version: number; provider: string }[];
  timeline: { version: number; updatedAt: string } | null;
  renders: {
    id: string;
    createdAt: string;
    job: { status: string; progress: number };
    output: { key: string } | null;
  }[];
}

export interface StatusResponse {
  id: string;
  status: string;
  step: PipelineStep | null;
  progress: number;
  error: string | null;
  /** The `AppError.code` behind `error`, which decides whether the screen has
   *  something to offer or only something to report. */
  errorCode: string | null;
  completedSteps: PipelineStep[];
  eta?: Eta | null;
  /** The processing job, so the page and the jobs menu count it down as one. */
  jobId?: string | null;
}

export interface TranscriptResponse {
  id: string;
  provider: string;
  language: string | null;
  text: string;
  segments: { id: string; index: number; start: number; end: number; text: string }[];
}

export interface ScriptResponse {
  id: string;
  version: number;
  provider: string;
  scenes: {
    id: string;
    index: number;
    script: string;
    sourceStart: number | null;
    sourceEnd: number | null;
    /** Silence held before this line. Absent on scripts written before pauses. */
    pauseBefore?: number;
  }[];
}

export interface RenderStatus {
  id: string;
  status: string;
  progress: number;
  error: string | null;
  output: { url: string; sizeBytes: number } | null;
  step?: string | null;
  eta?: Eta | null;
  /** The render's job, so the page and the jobs menu count it down as one. */
  jobId?: string | null;
  /** What this render is producing. Shown beside the progress. */
  width: number;
  height: number;
  fps: number;
}

/** Where a configured value came from. Shown beside every field. */
export type SettingSource = "saved" | "environment" | "unset";

export type ProviderMode = "individual" | "openrouter";
export type AiProviderId = "gemini" | "anthropic" | "openai" | "openrouter" | "elevenlabs";

export interface MaskedKey {
  masked: string | null;
  source: SettingSource;
}
export interface SettingValue {
  value: string | null;
  source: SettingSource;
}

export interface SettingsView {
  providerMode: ProviderMode;
  /** Returned whole rather than masked — none of it is a credential. */
  brandKit: BrandKit;
  openaiApiKey: MaskedKey;
  elevenLabsApiKey: MaskedKey;
  elevenLabsVoiceId: SettingValue;
  elevenLabsModel: SettingValue;
  elevenLabsFormat: SettingValue;
  geminiApiKey: MaskedKey;
  geminiModel: SettingValue;
  anthropicApiKey: MaskedKey;
  anthropicModel: SettingValue;
  openrouterApiKey: MaskedKey;
  openrouterVideoModel: SettingValue;
  openrouterScriptModel: SettingValue;
  transcriptionModel: SettingValue;
  updatedAt: string | null;
}

export interface ConnectionCheck {
  provider: AiProviderId;
  ok: boolean;
  detail: string;
}

export interface ModelGroup {
  /** Verified to work with this step. Others are offered but may fail. */
  recommended: string[];
  models: string[];
}

/** One recorded run of one pipeline step. The snapshot itself never travels. */
export interface StepRunSummary {
  id: string;
  jobId: string;
  jobType: string;
  step: string;
  status: "running" | "completed" | "failed" | "skipped";
  /** The job's attempt count when this ran — 1 on the first try — so a retry
   *  sits beside the failure before it rather than replacing it. */
  attempt: number;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  summary: Record<string, unknown>;
  error: string | null;
  costUsd: number;
  /** False once the snapshot has been pruned, and for steps that never keep one. */
  hasSnapshot: boolean;
}

export interface ScriptModels {
  /** What "Default script model" actually resolves to, so the option can say so. */
  defaultScriptModel?: string;
  /** False when the list could not be fetched and is the built-in fallback. */
  live: boolean;
  script: ModelGroup;
  /** Always OpenAI's: Anthropic writes, OpenAI transcribes. */
  transcription: ModelGroup;
}

export type ModelField = "gemini" | "anthropic" | "openrouterScript" | "openrouterVideo";

export interface ModelChoices {
  /** False when the list is the written-down fallback rather than the vendor's. */
  live: boolean;
  models: string[];
  recommended: string[];
}

/** One scene's window, as the review screen reads it. */
export interface CutWindow {
  sceneId: string;
  narration: string;
  /** The scene this window sits inside; a nudge may not leave it. */
  sceneStart: number;
  sceneEnd: number;
  start: number;
  end: number;
  anchor: string;
  reason: string;
  confidence: number | null;
  pinned?: boolean;
  candidates: { id: string; start: number; end: number; anchor: string; isDefault: boolean }[];
}

export interface CutReviewData {
  durationSeconds: number;
  decidedBy: string | null;
  keptSeconds: number | null;
  removedSeconds: number | null;
  windows: CutWindow[];
  filmstrip: { key: string; width: number; height: number } | null;
}

/**
 * What the account has to spend.
 *
 * `billing: false` is the self-hosted answer, and it is a different thing from
 * a zero balance — a zero balance on a product that bills reads as "you have
 * run out", which is the wrong thing to tell somebody running it for free on
 * their own machine. Every surface checks the flag, not the number.
 */
export type BillingView =
  | { billing: false }
  | {
      billing: true;
      freeRemaining: number;
      freeVideoLimit: number;
      balanceUsd: number;
      /** The team is free forever (PropelAuth `free_forever`): nothing is charged. */
      freeForever?: boolean;
      /** False when the deployment has accounts but no Stripe keys yet. */
      canTopUp: boolean;
      presetsUsd: readonly number[];
      ledger: Array<{
        id: string;
        kind: "purchase" | "export" | "render" | "revision" | "translation" | "refund" | "adjustment";
        amountUsd: number;
        projectName: string | null;
        durationSeconds: number | null;
        usedFreeVideo: boolean;
        /** Charged nothing because the team is free forever. */
        freeForever?: boolean;
        note: string | null;
        createdAt: string;
      }>;
    };

/** What one export will cost, asked before it is started. */
export type ExportQuote =
  | { billing: false }
  | {
      billing: true;
      providerCostUsd: number;
      priceUsd: number;
      willUseFreeVideo: boolean;
      alreadyPaid: boolean;
      affordable: boolean;
      balanceUsd: number;
      freeRemaining: number;
      freeVideoLimit: number;
      freeForever?: boolean;
    };

export type LanguageStatus = "translating" | "review" | "voicing" | "revoicing" | "ready" | "failed";

/** One translated line, beside the English it came from and the time it has. */
export interface LanguageLine {
  id: string;
  english: string;
  text: string;
  /** The most it may hold: words, or characters for a language counted that way. */
  budget: number;
  startAt: number;
  seconds: number;
  /** Too long for its time even sped up; a revoice is for these. */
  runsLong: boolean;
  /** Changed since it was last voiced. */
  edited: boolean;
  /** The voiced clip, once it is made. */
  clipKey?: string;
  clipSeconds?: number;
}

/** The words kept exactly as written in every translation. */
export interface KeepTermsView {
  terms: string[];
  /** The customer's own list, rather than the one found in the script. */
  custom: boolean;
  productName: string;
}

/** One of this video's languages, as `GET …/languages` shows it. */
export interface LanguageView {
  code: string;
  name: string;
  nativeName: string;
  direction: "ltr" | "rtl";
  status: LanguageStatus;
  /** How much of the video it translates. A made language keeps its old depth until its new translation lands. */
  depth: LanguageDepth;
  /** Whether it can be made as the whole video. */
  wholeVideo: boolean;
  /** The on-screen words, translated. */
  texts: TranslatedText[];
  /** The subtitles its voiced lines make. */
  cues: CaptionCue[];
  /** The English changed since it was made. */
  outOfDate: boolean;
  /** What it was or will be charged; today's quote for one that would have to be made again. */
  priceUsd: number;
  /** The whole-video quote for a made language. */
  videoPriceUsd: number;
  /** Whether "Translate the whole video" would be accepted now. */
  canRaise: boolean;
  /** While it is being made: how far along, the step it is on and the time left. Null otherwise. */
  progress: number | null;
  step: string | null;
  eta: Eta | null;
  /** The job making it, while it is being made, so the page and the jobs menu count it down as one. */
  jobId?: string | null;
  /** What it holds against the balance now. */
  heldUsd: number;
  /** Whether "Update" or "Try again" would be accepted now. */
  canRemake: boolean;
  /** Whether it may be discarded: a script in review, or a failed language nobody paid for. */
  canDiscard: boolean;
  lines: LanguageLine[];
  /** How many lines run long. */
  runsLong: number;
  error: string | null;
  downloads: { video?: string; audio?: string; vtt?: string; srt?: string; script?: string };
  /** This language as it is now; its browser export sends it back as `rendered`. */
  revision: string;
}

/** A language this video could be made in, quoted. */
export interface AvailableLanguage {
  code: string;
  name: string;
  nativeName: string;
  direction: "ltr" | "rtl";
  wholeVideo: boolean;
  /** The voice-and-subtitles quote. */
  priceUsd: number;
  /** The whole-video quote; the voice price where it cannot be made as one. */
  videoPriceUsd: number;
}

export interface LanguagesView {
  source: string;
  languages: LanguageView[];
  available: AvailableLanguage[];
  /** Null when nothing is billed. */
  balanceUsd: number | null;
  /** The team is free forever: every language is free. */
  freeForever?: boolean;
  canTranslate: boolean;
  /** Whether the whole video can be translated here: false for a launch film made before its words were kept. */
  canDrawWords?: boolean;
  reason?: "no_timeline" | "no_narration" | "version_not_ready";
}

/** How a project is shared: the settings, what the player will hold, and whether it can be published. */
export interface ShareSettings {
  slug: string;
  published: boolean;
  startIn: "viewer" | "source";
  subtitlesWhenMuted: boolean;
  emailGate: boolean;
  /** Launch videos only; null for a demo. */
  version: {
    chosen: { id: string; number: number } | null;
    playing: { id: string; number: number } | null;
    ready: { id: string; number: number }[];
  } | null;
  languages: { code: string; nativeName: string }[];
  notExported: { code: string; nativeName: string }[];
  canPublish: boolean;
  reason: string | null;
  linkPath: string;
  embedPath: string;
  /** Every view counted, all time. */
  views: number;
}

export type ShareStatsView = ShareStats & { hasLeads: boolean };

export interface ShareLeadsPage {
  leads: {
    email: string;
    firstSeenAt: string;
    lastSeenAt: string;
    views: number;
    furthestPercent: number;
    language: string;
  }[];
  total: number;
  page: number;
  pageSize: number;
}

export type ShareChange = Partial<
  Pick<ShareSettings, "published" | "startIn" | "subtitlesWhenMuted" | "emailGate"> & { launchVersionId: string }
>;

const versionQuery = (version?: string) => (version ? `?version=${encodeURIComponent(version)}` : "");

/** What a launch video is made from: a site, or a name and words, optionally with a site and a look. */
export type StartLaunchInput = (
  | {
      website: string;
      /** The site's logo or colours, replaced for this film only. Absent halves are the site's own. */
      brand?: {
        logoKey?: string;
        colors?: { accent: string; accentAlt: string; background: string; text: string };
      };
    }
  | {
      name: string;
      topic: string;
      website?: string;
      source?: "described" | "ai";
      look?:
        | {
            kind: "upload";
            logoKey: string;
            colors: { accent: string; accentAlt: string; background: string; text: string };
          }
        | { kind: "kit" }
        | { kind: "none" };
    }
) & { templateId?: string; seconds?: number };

export type SiteLookupAnswer =
  { found: true; name: string; summary: string; sources: { title: string; url: string }[] } | { found: false };

export const api = {
  /** How a project is shared. */
  share: (id: string) => request<ShareSettings>(`/api/projects/${id}/share`),

  /** Changes some of it. 409 `nothing_to_publish`, 400 `invalid_version`. */
  updateShare: (id: string, change: ShareChange) =>
    request<ShareSettings>(`/api/projects/${id}/share`, { method: "PATCH", body: JSON.stringify(change) }),

  /** Retires the address and makes another; every old link and embed stops. */
  newShareLink: (id: string) => request<ShareSettings>(`/api/projects/${id}/share/new-link`, { method: "POST" }),

  shareStats: (id: string, range: "30d" | "all") =>
    request<ShareStatsView>(`/api/projects/${id}/share/stats?range=${range}`),

  shareLeads: (id: string, page: number) => request<ShareLeadsPage>(`/api/projects/${id}/share/leads?page=${page}`),

  /** The emails as a CSV, fetched with the auth header so the page can save it. */
  shareLeadsCsv: (id: string) => requestBlob(`/api/projects/${id}/share/leads.csv`),

  listProjects: () => request<ProjectSummary[]>("/api/projects"),

  /** A video's languages, and a quote for each it does not have. `version` names a launch video's version. */
  languages: (id: string, version?: string) =>
    request<LanguagesView>(`/api/projects/${id}/languages${versionQuery(version)}`),

  /** Makes these languages, all or none. 402 `insufficient_credit`, 409 `already_exists` / `no_narration`. */
  makeLanguages: (
    id: string,
    body: { languages: string[]; review: boolean; depth?: LanguageDepth; version?: string },
  ) =>
    request<{ languages: LanguageView[] }>(`/api/projects/${id}/languages`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Edits translated lines: any in review, only the long ones once ready. 400 `line_too_long` with `{ id, budget }`. */
  editLanguage: (
    id: string,
    code: string,
    edits: { lines?: { id: string; text: string }[]; texts?: TranslatedText[] },
    version?: string,
  ) =>
    request<{ language: LanguageView }>(`/api/projects/${id}/languages/${code}${versionQuery(version)}`, {
      method: "PATCH",
      body: JSON.stringify(edits),
    }),

  /** Voices a reviewed script, or the edited long lines of a ready language (free). */
  voiceLanguage: (id: string, code: string, version?: string) =>
    request<{ language: LanguageView }>(`/api/projects/${id}/languages/${code}/voice${versionQuery(version)}`, {
      method: "POST",
    }),

  /**
   * Discards a script in review or a failed language, releasing what it holds.
   * A made language whose update is discarded comes back as it was (`language`);
   * any other is removed (`null`). 409 `not_discardable` for anything else.
   */
  discardLanguage: (id: string, code: string, version?: string) =>
    request<{ language: LanguageView | null }>(`/api/projects/${id}/languages/${code}${versionQuery(version)}`, {
      method: "DELETE",
    }),

  /**
   * Makes a language again at today's quote: an out-of-date one, or a ready
   * voice one raised to the whole video (`depth`). Without a depth it keeps its own.
   */
  updateLanguage: (id: string, code: string, body: { review: boolean; depth?: LanguageDepth }, version?: string) =>
    request<{ language: LanguageView }>(`/api/projects/${id}/languages/${code}/update${versionQuery(version)}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * A link to every ready language's files as one zip, good for a few minutes
   * without a session. The page navigates to it, so the browser saves the zip
   * as it streams instead of the page holding it.
   */
  /** The words every translation keeps as written: the customer's list, or the one found in the script. */
  keepTerms: (id: string) => request<KeepTermsView>(`/api/projects/${id}/keep-terms`),
  /** Saves the customer's list; null goes back to the found one. */
  saveKeepTerms: (id: string, terms: string[] | null) =>
    request<KeepTermsView>(`/api/projects/${id}/keep-terms`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ terms }),
    }),

  languagesDownloadLink: (id: string, version?: string) =>
    request<{ url: string; expiresAt: string }>(`/api/projects/${id}/languages/download-link${versionQuery(version)}`),

  getProject: (id: string) => request<ProjectDetail>(`/api/projects/${id}`),

  cutReview: (id: string) => request<CutReviewData>(`/api/projects/${id}/cut`),

  saveCut: (id: string, windows: { sceneId: string; start: number; end: number; pinned?: boolean }[]) =>
    request<{ status: string; rebuilt: boolean; jobId?: string; keptSeconds: number }>(`/api/projects/${id}/cut`, {
      method: "PUT",
      body: JSON.stringify({ windows }),
    }),

  replanCut: (id: string) =>
    request<{ status: string; jobId: string }>(`/api/projects/${id}/cut/replan`, { method: "POST" }),

  createProject: (input: {
    name: string;
    tone?: string;
    audience?: string;
    /** An id from `videoPurposes`. */
    purpose?: string;
    instructions?: string;
  }) => request<{ id: string }>("/api/projects", { method: "POST", body: JSON.stringify(input) }),

  /**
   * Uploads with XHR rather than fetch: a recording can be hundreds of
   * megabytes, and upload progress is the only honest thing to show while it
   * travels.
   */
  upload(id: string, file: File, onProgress?: (fraction: number) => void, narrate = true) {
    return new Promise<{ jobId: string }>((resolve, reject) => {
      const form = new FormData();
      form.append("file", file);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `/api/projects/${id}/upload${narrate ? "" : "?narrate=0"}`);
      // This one does not go through `request`, so it has to carry the token
      // itself — and it is the single call that most needs to: an upload
      // rejected for want of a header fails after the bytes have travelled.
      for (const [name, value] of Object.entries(authHeader())) xhr.setRequestHeader(name, value);
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) onProgress?.(event.loaded / event.total);
      });
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText) as { jobId: string });
        } else {
          let message = "Upload failed";
          // Keep the server's own code where it sent one: the caller needs to
          // tell an unconfigured provider from a rejected file, and answers
          // them differently.
          let code = "upload_failed";
          try {
            const body = JSON.parse(xhr.responseText) as ApiErrorBody;
            message = body.error;
            if (body.code) code = body.code;
          } catch {
            /* keep the generic message */
          }
          reject(new ApiError(message, xhr.status, code));
        }
      });
      xhr.addEventListener("error", () => reject(new ApiError("Upload failed", 0, "network")));
      xhr.send(form);
    });
  },

  /**
   * A still for a HEAD or END card.
   *
   * Plain fetch rather than the XHR the recording upload uses: a card image is
   * small enough that a progress bar would be gone before it was read.
   */
  uploadImage(id: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<{ key: string; filename: string; url: string }>(`/api/projects/${id}/images`, {
      method: "POST",
      body: form,
    });
  },

  /**
   * A font file for this project's cards.
   *
   * Returns the family name the card should ask for, derived from the filename
   * on the server so the editor and the renderer cannot disagree about it.
   */
  uploadFont(id: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<{ key: string; family: string; filename: string; url: string }>(`/api/projects/${id}/fonts`, {
      method: "POST",
      body: form,
    });
  },

  cost: (id: string) =>
    request<{
      totalUsd: number;
      items: {
        provider: string;
        kind: string;
        unit: string;
        quantity: number;
        inputTokens: number;
        outputTokens: number;
        costUsd: number;
      }[];
    }>(`/api/projects/${id}/cost`),

  renameProject: (id: string, name: string) =>
    request<{ id: string; name: string }>(`/api/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  /** An empty string clears the override and returns the project to the global default. */
  /**
   * What kind of video this is. Saved on its own rather than with the script,
   * because it is a project setting and folding it into Save would tie a
   * configuration change to whether the text happens to be dirty.
   */
  /**
   * The settings that shape the script: what kind of video it is, how it should
   * sound, who it is for. Saved on their own rather than with the script text,
   * because they are project settings and folding them into Save would tie a
   * configuration change to whether the words happen to be dirty.
   */
  setProjectWriting: (id: string, patch: { purpose?: string; tone?: string; audience?: string }) =>
    request<ProjectDetail>(`/api/projects/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  setProjectScriptModel: (id: string, scriptModel: string) =>
    request<{ id: string; scriptModel: string | null }>(`/api/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ scriptModel }),
    }),

  settings: () => request<SettingsView>("/api/settings"),

  /** Balance, free videos and the statement. One call, because one screen. */
  /**
   * This account's team, and the request that makes sure it has one.
   *
   * The auth hook creates a team for an account that has none, so asking this
   * is what guarantees one exists before the browser goes looking for a token
   * that knows about it.
   */
  /**
   * A credential for the recorder extension, returned exactly once.
   *
   * Issuing a new one replaces the last, so this is called only when the
   * extension says it has none — otherwise every page load would unpair
   * whichever other browser was holding the previous token.
   */
  recorderToken: () => request<{ token: string }>("/api/recorder/token", { method: "POST" }),

  team: () => request<{ orgId: string | null }>("/api/team"),

  billing: () => request<BillingView>("/api/billing"),

  /**
   * Starts a top-up and returns where to send the browser.
   *
   * The caller navigates to the URL; it does not open in a tab. Stripe's page
   * is where the person is now, and coming back is a navigation rather than
   * closing something.
   */
  startTopUp: (amountUsd: number) =>
    request<{ url: string }>("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ amountUsd }),
    }),

  /**
   * Runs the pipeline again on a recording that is already uploaded.
   *
   * Every kind of failure leaves a project whose file is fine and whose
   * pipeline is not — a provider that timed out, a worker that died, a
   * recording too long for the free tier. All of them were a dead end until
   * this existed, because uploading was the only thing that started work.
   */
  reprocess: (id: string) =>
    request<{ status: string; jobId: string }>(`/api/projects/${id}/process`, { method: "POST" }),

  /**
   * Asks the server to check a finished checkout and grant the credit.
   *
   * Called on the way back from Stripe, with the id from the URL. The redirect
   * proves nothing — the server asks Stripe whether that session was paid and
   * whether it belongs to this account — so this is a prompt, not a claim.
   *
   * It exists because a webhook that does not arrive is silent: the payment
   * succeeds, the receipt arrives, and the balance does not move. The webhook
   * is still the normal path and still the only one that works when nobody has
   * a browser open; this is what makes a missed one recoverable.
   */
  confirmTopUp: (sessionId: string) =>
    request<{ granted: boolean; amountUsd?: number; reason: string }>("/api/billing/confirm", {
      method: "POST",
      body: JSON.stringify({ sessionId }),
    }),

  /** What this export will cost, before pressing the button. */
  exportQuote: (id: string) => request<ExportQuote>(`/api/projects/${id}/export-quote`),

  /**
   * Every asset key resolved to a directly fetchable URL.
   *
   * For client-side rendering only. The ordinary `/assets/<key>` path redirects
   * to the bucket, and a preflighted CORS request — which any `Range` fetch is
   * — cannot follow a cross-origin redirect.
   */
  /**
   * Hands a browser-rendered video to the API, which makes it a real export.
   *
   * XHR rather than `request`, for the same reason `upload` is: a finished
   * render is tens of megabytes and the screen should say how far along the
   * transfer is rather than sit still.
   */
  submitBrowserRender: (
    id: string,
    blob: Blob,
    meta: {
      width: number;
      height: number;
      fps: number;
      durationSeconds: number;
      language?: string;
      /** For a language: the view's `revision` it was rendered from. */
      rendered?: string;
    },
    onProgress?: (fraction: number) => void,
  ) =>
    new Promise<{ renderId: string; url: string; downloadUrl: string; filename: string | null; sizeBytes: number }>(
      (resolve, reject) => {
        const body = new FormData();
        body.append("width", String(meta.width));
        body.append("height", String(meta.height));
        body.append("fps", String(meta.fps));
        body.append("durationSeconds", String(meta.durationSeconds));
        // Before the file: the API reads fields only up to the file part.
        if (meta.language) body.append("language", meta.language);
        if (meta.rendered) body.append("rendered", meta.rendered);
        body.append("file", blob, "render.mp4");

        const xhr = new XMLHttpRequest();
        xhr.open("POST", `/api/projects/${id}/render/browser`);
        for (const [name, value] of Object.entries(authHeader())) xhr.setRequestHeader(name, value);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) onProgress?.(event.loaded / event.total);
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText) as never);
            return;
          }
          let body: ApiErrorBody = { error: xhr.statusText, code: "http_error" };
          try {
            body = JSON.parse(xhr.responseText) as ApiErrorBody;
          } catch {
            // A non-JSON failure from the proxy rather than from the API.
          }
          reject(new ApiError(body.error, xhr.status, body.code, body.details));
        };
        xhr.onerror = () => reject(new ApiError("Could not reach the API", 0, "unreachable"));
        xhr.send(body);
      },
    ),

  assetUrls: (id: string) =>
    request<{ urls: Record<string, string>; expiresInSeconds: number }>(`/api/projects/${id}/asset-urls`),

  /**
   * Reads a brand kit off a website. Answers with a guess; saves nothing.
   *
   * On the server because a browser cannot fetch another origin's HTML, and
   * because the fetch needs fencing — see apps/api/src/routes/brand.ts.
   */
  brandFromWebsite: (website: string) =>
    request<{
      website: string;
      name: string | null;
      logo: string | null;
      colors: { text: string; background: string; accent: string; accentAlt: string };
      titleFont: string | null;
      bodyFont: string | null;
      /** What the site is set in, and whether we ship it. Null when nothing was readable. */
      typeface: { name: string; rendered: boolean } | null;
      /** Stored for the kit to keep. Null when the site offered nothing usable. */
      logoKey: string | null;
      logoUrl: string | null;
      /** The logo's bytes, for a preview when it could not be stored. */
      logoDataUri: string | null;
      found: { colours: number; declaredColour: boolean; fonts: number; logo: boolean; logoBytes: boolean };
      /** Whether the page had enough words to make a film from. */
      foundText: boolean;
    }>("/api/brand/from-website", { method: "POST", body: JSON.stringify({ website }) }),

  /**
   * What a company makes, written by AI from public pages, for a site that
   * would not let us read it. Not charged; ten an hour.
   */
  lookupSite: (website: string) =>
    request<SiteLookupAnswer>("/api/launch-video/lookup", { method: "POST", body: JSON.stringify({ website }) }),

  /**
   * Sets the workspace logo from a file the person chose.
   *
   * A data URI because that is what the browser's own file reader produces,
   * and a logo is small enough that it costs nothing to send one.
   */
  /**
   * Starts a launch video and returns at once.
   *
   * Either a website or a name and a sentence — the two ways in. A described
   * request may also carry the website it was written about; the brief is
   * what the film is made from, and the address only gives it a reference.
   */
  startLaunchVideo: (input: StartLaunchInput) =>
    request<{
      projectId: string;
      jobId: string;
      name: string;
      foundPageText: boolean;
      templateId: string;
      templateName: string;
      logo: "kit" | "website" | "upload" | "wordmark";
    }>("/api/launch-video", { method: "POST", body: JSON.stringify(input) }),

  /** A launch video's versions, its script and what the next one costs. */
  launchVersions: (projectId: string) => request<LaunchVersionsView>(`/api/projects/${projectId}/versions`),

  /**
   * Makes the next version from `from` — from an edited script, or from a
   * change asked for in words. With neither, and `from` a failed first film,
   * tries that film again (charged as the launch video, gated like a new one). Refused with 409 `already_running` while one is
   * being made and 402 `insufficient_credit` when the balance cannot cover it.
   */
  makeLaunchVersion: (projectId: string, body: { from: string; script?: string; message?: string }) =>
    request<{ versionId: string; number: number; priceUsd: number; jobId: string }>(
      `/api/projects/${projectId}/versions`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  /** The narrators this ElevenLabs account offers, with previews. */
  listNarrators: () => request<{ narrators: Narrator[] }>("/api/voices"),

  uploadBrandLogo: (dataUri: string) =>
    request<{ logoKey: string; logoUrl: string }>("/api/brand/logo", {
      method: "POST",
      body: JSON.stringify({ dataUri }),
    }),

  /**
   * Fields left undefined keep their current value; an empty string clears one
   * back to the environment. The three states are distinct on purpose.
   */
  saveSettings: (update: Partial<Record<string, unknown>> & { providerMode?: ProviderMode }) =>
    request<SettingsView>("/api/settings", { method: "PUT", body: JSON.stringify(update) }),

  /**
   * Proves one provider's credentials. `apiKey` tests a value before it is
   * saved; without it the stored one is used. The key never comes back.
   */
  testProvider: (provider: AiProviderId, apiKey?: string) =>
    request<ConnectionCheck>(`/api/settings/test/${provider}`, {
      method: "POST",
      body: JSON.stringify(apiKey ? { apiKey } : {}),
    }),

  /**
   * The saved key, in full. Asked for on a click, never on page load — a key
   * that arrives with the page is in the DOM every time the page is opened.
   */
  revealKey: (provider: AiProviderId) => request<{ key: string }>(`/api/settings/key/${provider}`),

  /** What can be chosen for one model field. One field, one vendor, one call. */
  modelChoices: (field: ModelField) => request<ModelChoices>(`/api/settings/models/${field}`),

  /** Narrators the configured key can reach, for choosing the global default. */
  settingsVoices: () =>
    request<{ provider: string; voices: { id: string; name: string; category: string; freeTier: boolean }[] }>(
      "/api/settings/voices",
    ),

  deleteProject: (id: string) =>
    request<{ deleted: boolean; assetsRemoved: number; assetsTotal: number }>(`/api/projects/${id}`, {
      method: "DELETE",
    }),

  actions: (id: string) =>
    request<{ id: string; timestamp: number; type: string; label: string | null; source: string }[]>(
      `/api/projects/${id}/actions`,
    ),

  status: (id: string) => request<StatusResponse>(`/api/projects/${id}/status`),
  transcript: (id: string) => request<TranscriptResponse>(`/api/projects/${id}/transcript`),
  script: (id: string) => request<ScriptResponse>(`/api/projects/${id}/script`),

  updateScript: (
    id: string,
    scenes: { id: string; script: string; pauseBefore?: number }[],
    regenerateVoice: boolean,
  ) =>
    request<{ saved: boolean; jobId: string | null }>(`/api/projects/${id}/script`, {
      method: "PATCH",
      body: JSON.stringify({ scenes, regenerateVoice }),
    }),

  voices: (id: string) =>
    request<{ provider: string; voices: { id: string; name: string; category: string; freeTier: boolean }[] }>(
      `/api/projects/${id}/voices`,
    ),

  requestProxy: (id: string, width: number) =>
    request<{ status: string; key: string | null; jobId: string | null }>(`/api/projects/${id}/proxy`, {
      method: "POST",
      body: JSON.stringify({ width }),
    }),

  /**
   * One job's progress. Poll this rather than the project's status: several
   * job types never move the project off `ready`, so a client watching that
   * is told the work finished before it started.
   */
  jobStatus: (id: string, jobId: string) =>
    request<{
      id: string;
      type: string;
      status: string;
      step: string | null;
      progress: number;
      /** From a job that succeeded without doing what was asked. */
      note: string | null;
      error: string | null;
      finished: boolean;
    }>(`/api/projects/${id}/jobs/${jobId}`),

  setAutoCut: (id: string, enabled: boolean) =>
    request<{ status: string; jobId: string }>(`/api/projects/${id}/autocut`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),

  /** Re-plans cuts after "Keep original audio" changes, without rewriting the script. */
  recutForVoice: (id: string, voiceOn: boolean) =>
    request<{ status: string; jobId?: string }>(`/api/projects/${id}/autocut`, {
      method: "POST",
      body: JSON.stringify({ enabled: true, voiceOn, realign: false }),
    }),

  /** The built-in narration prompt and this project's edit of it, if any. */
  scriptPrompt: (id: string) =>
    request<{ default: string; custom: string | null; using: string }>(`/api/projects/${id}/script/prompt`),

  /**
   * `prompt` replaces the built-in one and is remembered; "" forgets it.
   * `voice` and `tighten` queue the follow-on work once the script exists.
   */
  regenerateScript: (id: string, options: { prompt?: string; voice?: boolean; tighten?: boolean } = {}) =>
    request<{ status: string; jobId: string }>(`/api/projects/${id}/script/regenerate`, {
      method: "POST",
      body: JSON.stringify(options),
    }),

  /**
   * `sections` narrows it to one part of the video. Omitted means all of them,
   * which is what the server has always defaulted to.
   */
  generateVoice: (id: string, voiceId?: string, sections?: ("head" | "body" | "end")[]) =>
    request<{ status: string; jobId: string }>(`/api/projects/${id}/voice`, {
      method: "POST",
      body: JSON.stringify({ ...(voiceId ? { voiceId } : {}), ...(sections ? { sections } : {}) }),
    }),

  timeline: (id: string) => request<{ version: number; data: Timeline }>(`/api/projects/${id}/timeline`),

  saveTimeline: (id: string, timeline: Timeline) =>
    request<{ saved: boolean; version: number; durationSeconds: number }>(`/api/projects/${id}/timeline`, {
      method: "PUT",
      body: JSON.stringify({ timeline, createdBy: "editor" }),
    }),

  render: (id: string, options: { width: number; height: number; fps: number }) =>
    request<{ id: string; jobId: string; status: string }>(`/api/projects/${id}/render`, {
      method: "POST",
      body: JSON.stringify(options),
    }),

  /**
   * The render already running for this project, if any.
   *
   * A render outlives the tab that started it, and the tab was the only thing
   * that knew its id — so a reload lost the progress bar while the render
   * carried on underneath. This is how a freshly loaded page finds it again.
   */
  currentRender: (id: string) =>
    request<{
      id: string;
      status: string;
      progress: number | null;
      startedAt: string | null;
      width: number;
      height: number;
      fps: number;
    } | null>(`/api/projects/${id}/render/current`),

  /** Stops a render. The worker notices on its next frame. */
  cancelRender: (id: string, renderId: string) =>
    request<{ id: string; status: "cancelled" }>(`/api/projects/${id}/render/${renderId}/cancel`, {
      method: "POST",
    }),
  renderStatus: (id: string, renderId: string) => request<RenderStatus>(`/api/projects/${id}/render/${renderId}`),

  exports: (id: string) =>
    request<
      {
        id: string;
        url: string;
        sizeBytes: number;
        durationSeconds: number;
        createdAt: string;
        width: number;
        height: number;
        fps: number;
        format: string;
      }[]
    >(`/api/projects/${id}/exports`),

  /**
   * Which providers are actually live, saved settings included.
   *
   * Answers a question the screens need before they let someone start work:
   * a missing key does not fail, it substitutes a mock, and the mock's output
   * is convincing enough to be mistaken for the real thing.
   */
  ready: () =>
    request<{ ready: boolean; providers: Record<string, string>; unconfigured: ContentProvider[] }>("/ready"),

  /**
   * One run with the output it produced.
   *
   * 404 when the snapshot has aged out — a normal answer for an old run, not
   * an error, so callers say "aged out" rather than surfacing it.
   */
  stepOutput: (id: string, runId: string) =>
    request<{ step: string; snapshot: unknown }>(`/api/projects/${id}/steps/${runId}`),

  /** Re-runs the visual analysis alone, after every model was busy. */
  analyse: (id: string) =>
    request<{ status: string; jobId: string }>(`/api/projects/${id}/analyse`, { method: "POST" }),

  steps: (id: string) => request<{ runs: StepRunSummary[] }>(`/api/projects/${id}/steps`),

  /**
   * This run against the previous completed run of the same step. 404 is a
   * normal answer — a first run, a pruned snapshot, or a step with nothing
   * comparable to show — so callers explain it rather than surfacing it.
   */
  stepDiff: (id: string, runId: string) => request<StepDiff>(`/api/projects/${id}/steps/${runId}/diff`),

  output: (id: string) =>
    request<{
      url: string;
      /** Carries ?download, so the response tells the browser to save it. */
      downloadUrl: string;
      filename: string | null;
      sizeBytes: number;
      durationSeconds: number;
      renderId: string;
    }>(`/api/projects/${id}/output`),
};

// --- Admin. Mirrors packages/database/src/admin.ts; the web app does not import the database package. ---

export type AdminFilter = "all" | "stuck" | "paid" | "free_forever" | "recent";

export type AdminLatest = { at: string; type: "attempt" | "job"; label: string; outcome: string; failed: boolean };

export type AdminTeamRow = {
  orgId: string;
  name: string | null;
  members: number;
  /** The most recently seen member's. */
  email: string;
  /** ISO. */
  lastSeen: string;
  latest: AdminLatest | null;
  /** Projects with status "completed". */
  films: number;
  attempts: number;
  /** The sum of purchase entries. */
  paidUsd: number;
  balanceUsd: number;
  /** UsageEvent for the team's projects, plus Attempt.costUsd. */
  costUsd: number;
  freeForever: boolean;
};

export type AdminTeamsPage = {
  teams: AdminTeamRow[];
  counts: Record<AdminFilter, number>;
  nextOffset: number | null;
};

export type AdminTeamDetail = {
  orgId: string;
  name: string | null;
  createdAt: string;
  members: { email: string; name: string | null; lastSeen: string }[];
  freeVideos: { used: number; limit: number };
  freeForever: boolean;
  brandKitSet: boolean;
  totals: {
    balanceUsd: number;
    paidUsd: number;
    spentUsd: number;
    films: number;
    attempts: number;
    failures: number;
    costUsd: number;
  };
};

export type AdminEventType = "attempt" | "project" | "job" | "credit";
export type AdminEvent = {
  id: string;
  at: string;
  type: AdminEventType;
  /** Email. */
  who: string | null;
  title: string;
  outcome: string | null;
  failed: boolean;
  /** What it cost us. */
  costUsd: number | null;
  /** Money in (+) or out (-) of their balance. */
  amountUsd: number | null;
  projectId: string | null;
  detail: Record<string, unknown>;
};
export type AdminEventsFilter = "all" | "attempts" | "failures" | "projects" | "money";

/**
 * Whether the signed-in person is an admin, as a definite answer or none.
 * True on a 200 saying so, false on the API's 404 (or a 200 saying no), and
 * null when the question went unanswered (network, 5xx), so a caller can ask again.
 */
export async function askAdminMe(): Promise<boolean | null> {
  try {
    return (await request<{ admin: boolean }>("/api/admin/me")).admin === true;
  } catch (error) {
    return error instanceof ApiError && error.status === 404 ? false : null;
  }
}

/** Whether the signed-in person is an admin. Any error, including the API's 404, means no. */
export async function getAdminMe(): Promise<boolean> {
  return (await askAdminMe()) === true;
}

export function getAdminTeams(input: { q?: string; filter?: AdminFilter; offset?: number }): Promise<AdminTeamsPage> {
  const params = new URLSearchParams();
  if (input.q) params.set("q", input.q);
  if (input.filter && input.filter !== "all") params.set("filter", input.filter);
  if (input.offset) params.set("offset", String(input.offset));
  return request(`/api/admin/teams${params.size ? `?${params}` : ""}`);
}

export function getAdminTeam(orgId: string): Promise<AdminTeamDetail> {
  return request(`/api/admin/teams/${encodeURIComponent(orgId)}`);
}

export function getAdminEvents(
  orgId: string,
  input: { type?: AdminEventsFilter; before?: string },
): Promise<{ events: AdminEvent[] }> {
  const params = new URLSearchParams();
  if (input.type && input.type !== "all") params.set("type", input.type);
  if (input.before) params.set("before", input.before);
  return request(`/api/admin/teams/${encodeURIComponent(orgId)}/events${params.size ? `?${params}` : ""}`);
}

/** Mirrors `AdminJobDebug` in packages/database/src/admin.ts. */
export type AdminJobDebug = {
  job: {
    id: string;
    projectId: string;
    projectName: string;
    orgId: string | null;
    type: string;
    status: string;
    step: string | null;
    errorCode: string | null;
    error: string | null;
    attempts: number;
    maxAttempts: number;
    createdAt: string;
    startedAt: string | null;
    failedAt: string | null;
    asked: Record<string, unknown>;
  };
  failures: {
    at: string;
    attempt: number;
    step: string | null;
    message: string;
    code: string | null;
    retryable: boolean;
    stack: string | null;
    details: unknown;
  }[];
  steps: {
    attempt: number;
    step: string;
    status: string;
    startedAt: string;
    durationMs: number | null;
    summary: unknown;
    error: string | null;
  }[];
  calls: {
    at: string;
    provider: string;
    kind: string;
    operation: string | null;
    model: string | null;
    quantity: number;
    unit: string;
    inputTokens: number | null;
    outputTokens: number | null;
    costUsd: number;
  }[];
  recording: {
    key: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    durationSeconds: number | null;
    width: number | null;
    height: number | null;
    uploadedAt: string;
  } | null;
};

/** Everything recorded about one failed job. 404 for a job that did not fail. */
export function getAdminJobDebug(jobId: string): Promise<AdminJobDebug> {
  return request(`/api/admin/jobs/${encodeURIComponent(jobId)}`);
}
